import React, { useState, useEffect } from 'react';
import {
    View,
    Text,
    TouchableOpacity,
    StyleSheet,
    ScrollView,
    Dimensions,
    FlatList,
    Modal,
    TextInput,
    Platform,
  RefreshControl,
} from 'react-native';
import {
  Button,
    Header,
    BottomTabs,
    ScreenLayout,
    colors,
    spacing,
    typography
} from '../components';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { Ionicons } from '@expo/vector-icons';
import { apiService } from '../services/api';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

// Detect if we're on mobile browser (not just web platform)
const isMobileBrowser = Platform.OS === 'web' && (screenWidth < 768 || screenHeight < 768);

// Responsive card sizing - using Home screen's proven approach
const getCardWidth = (type: 'platform' | 'action' | 'campaign') => {
  if (isMobileBrowser) {
    // Mobile browser gets mobile app layout with better sizing
    switch (type) {
      case 'platform': return screenWidth * 0.4;
      case 'action': return Math.max(110, screenWidth * 0.28); // Larger width for comfortable mobile browser experience
      case 'campaign': return screenWidth * 0.7;
      default: return screenWidth * 0.4;
    }
  } else if (Platform.OS === 'web') {
    // Desktop web gets optimized layout
    switch (type) {
      case 'platform': return Math.min(320, screenWidth * 0.22);
      case 'action': return Math.min(140, screenWidth * 0.08); // Smaller width for 8 actions in 1-line layout
      case 'campaign': return Math.min(400, screenWidth * 0.28);
      default: return 320;
    }
  } else {
    // Native mobile app - use larger width for better mobile experience
    switch (type) {
      case 'platform': return screenWidth * 0.4;
      case 'action': return Math.max(110, screenWidth * 0.28); // Larger width for comfortable mobile experience (110px minimum or 28% of screen)
      case 'campaign': return screenWidth * 0.7;
      default: return screenWidth * 0.4;
    }
  }
};

export const SocialHubScreen: React.FC = () => {
  const { user, currentBusiness } = useAppStore();
    const { showNotification, activeTab, setActiveTab, navigateTo } = useAppContext();
  const insets = useSafeAreaInsets();
  
  // State management - using Home screen's pattern
  const [refreshing, setRefreshing] = useState(false);
  const [showRightPane] = useState(true); // Always show right pane
  const [connectedPlatforms, setConnectedPlatforms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
    const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
    const [showAddAccount, setShowAddAccount] = useState(false);

  // WhatsApp form state
    const [accountName, setAccountName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showVerification, setShowVerification] = useState(false);
    const [registrationId, setRegistrationId] = useState('');

    // Set the active tab to 'social' when this screen loads
    useEffect(() => {
        setActiveTab('social');
    }, [setActiveTab]);

  // Load connected platforms data
    useEffect(() => {
    loadConnectedPlatforms();
    }, []);

  const loadConnectedPlatforms = async () => {
    try {
      setLoading(true);
      // Simulate API calls for different platforms with enhanced data
    const platforms = [
        {
            id: 'whatsapp',
          name: 'WhatsApp Business',
            icon: 'logo-whatsapp',
          status: 'connected',
          stats: { 
            conversations: 24, 
            messages: 156,
            responseTime: '2.3s',
            satisfaction: 4.8,
            monthlyGrowth: 15
          },
            color: '#25D366',
          lastActive: '2 hours ago',
          health: 'excellent',
          features: ['Automated Messages', 'Media Sharing', 'Quick Replies']
        },
        {
            id: 'facebook',
          name: 'Facebook Messenger',
            icon: 'logo-facebook',
          status: 'connected',
          stats: { 
            conversations: 12, 
            messages: 89,
            responseTime: '4.1s',
            satisfaction: 4.2,
            monthlyGrowth: 8
          },
            color: '#1877F2',
          lastActive: '1 hour ago',
          health: 'good',
          features: ['Rich Media', 'Quick Replies', 'Persistent Menu']
        },
        {
          id: 'telegram',
          name: 'Telegram',
          icon: 'send',
          status: 'disconnected',
          stats: { 
            conversations: 0, 
            messages: 0,
            responseTime: 'N/A',
            satisfaction: 0,
            monthlyGrowth: 0
          },
          color: '#0088CC',
          lastActive: 'Never',
          health: 'offline',
          features: ['Bot Commands', 'File Sharing', 'Group Chats']
        },
        {
          id: 'email',
          name: 'Email',
          icon: 'mail',
          status: 'disconnected',
          stats: {
            conversations: 0,
            messages: 0,
            responseTime: 'N/A',
            satisfaction: 0,
            monthlyGrowth: 0
          },
          color: '#EA4335',
          lastActive: 'Never',
          health: 'offline',
          features: ['Rich HTML', 'Attachments', 'Templates']
        },
        {
          id: 'x-twitter',
          name: 'X (Twitter)',
          icon: 'logo-twitter',
          status: 'disconnected',
          stats: {
            conversations: 0,
            messages: 0,
            responseTime: 'N/A',
            satisfaction: 0,
            monthlyGrowth: 0
          },
          color: '#000000',
          lastActive: 'Never',
          health: 'offline',
          features: ['Direct Messages', 'Mentions', 'Quick Replies', 'Auto-Reply']
        }
      ];

      // Overlay live X Twitter status from API
      try {
        const xStatus = await apiService.getXTwitterStatus();
        if (xStatus?.data?.isConnected) {
          const xPlatform = platforms.find(p => p.id === 'x-twitter');
          if (xPlatform) {
            xPlatform.status = 'connected';
            xPlatform.health = xStatus.data.expiredAccounts > 0 ? 'warning' : 'good';
            xPlatform.lastActive = 'Active';
          }
        }
      } catch (_) { /* non-fatal */ }

      setConnectedPlatforms(platforms);
    } catch (error) {
      console.error('Error loading platforms:', error);
      showNotification('error', 'Error', 'Failed to load platform data');
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadConnectedPlatforms();
    setRefreshing(false);
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const handlePlatformPress = (platform: any) => {
    // Navigate to dedicated setup screens for supported channels
    if (platform.id === 'x-twitter') {
      navigateTo('x-twitter-setup');
      return;
    }
    if (platform.id === 'facebook') {
      navigateTo('facebook-messenger-setup');
      return;
    }
    if (platform.id === 'whatsapp') {
      navigateTo('whatsapp-business-setup');
      return;
    }
    if (platform.status === 'connected') {
      showNotification('info', 'Platform', `${platform.name} is connected and active`);
    } else {
      setSelectedPlatform(platform.id);
    }
  };

  const openDeveloperPlayground = async () => {
    try {
      const { setPendingSettingsItem } = await import('../utils/tempStorage');
      await setPendingSettingsItem('developer');
      navigateTo('settings');
    } catch (error) {
      console.error('❌ [SocialHubScreen] Failed to open developer settings:', error);
      showNotification('error', 'Navigation Error', 'Unable to open Developer Access right now.');
    }
  };

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'new-conversation':
        navigateTo('inbox' as any);
        break;
      case 'create-campaign':
        showNotification('info', 'Campaign', 'Campaign creation is being configured. Please contact support for assistance.');
        break;
      case 'analytics':
        showNotification('info', 'Analytics', 'Analytics dashboard is being configured. Please contact support for assistance.');
        break;
      case 'templates':
        showNotification('info', 'Templates', 'Message templates are being configured. Please contact support for assistance.');
        break;
      default:
        showNotification('info', 'Action', `${action} feature is being configured. Please contact support for assistance.`);
    }
  };

  // Modal close functions
  const closePlatformModal = () => {
    setSelectedPlatform(null);
  };

  const closeAddAccountModal = () => {
    setShowAddAccount(false);
    setAccountName('');
    setPhoneNumber('');
    setVerificationCode('');
    setError('');
    setShowVerification(false);
    setRegistrationId('');
  };

  const closeVerificationModal = () => {
    setShowVerification(false);
    setVerificationCode('');
    setError('');
  };

  // Quick actions data
  const quickActions = [
    { id: 'new-conversation', title: 'New Conversation', icon: 'chatbubble-outline' },
    { id: 'create-campaign', title: 'Create Campaign', icon: 'megaphone-outline' },
    { id: 'analytics', title: 'Analytics', icon: 'analytics-outline' },
    { id: 'templates', title: 'Templates', icon: 'document-text-outline' },
  ];

  // Render platform widget - enhanced with more details and progress bars
  const renderPlatformWidget = ({ item }: { item: any }) => {
    const getHealthColor = (health: string) => {
      switch (health) {
        case 'excellent': return colors.success;
        case 'good': return '#FFA500';
        case 'poor': return colors.error;
        default: return colors.text.secondary;
      }
    };

    const getHealthIcon = (health: string) => {
      switch (health) {
        case 'excellent': return 'checkmark-circle';
        case 'good': return 'warning';
        case 'poor': return 'alert-circle';
        default: return 'help-circle';
      }
    };

            return (
                        <TouchableOpacity 
        style={styles.platformWidget}
        onPress={() => handlePlatformPress(item)}
        activeOpacity={0.7}
      >
        {/* Header with icon, status, and health indicator */}
        <View style={styles.platformHeader}>
          <View style={styles.platformIconRow}>
            <View style={[styles.platformIconContainer, { backgroundColor: item.color }]}>
              <Ionicons name={item.icon} size={24} color={colors.white} />
                    </View>
            {item.status === 'connected' && (
              <View style={[styles.healthIndicator, { backgroundColor: getHealthColor(item.health) }]}>
                <Ionicons name={getHealthIcon(item.health)} size={12} color={colors.white} />
                </View>
            )}
                    </View>
          <View style={[
            styles.statusBadge,
            { backgroundColor: item.status === 'connected' ? colors.success : colors.error }
          ]}>
            <Text style={styles.statusText}>
              {item.status === 'connected' ? 'Connected' : 'Disconnected'}
                        </Text>
                    </View>
                </View>
        
        {/* Platform name */}
        <Text style={styles.platformTitle}>{item.name}</Text>
        
        {/* Main stats */}
        <View style={styles.platformStats}>
          <View style={styles.statRow}>
            <View style={styles.statItem}>
              <Text style={styles.platformMainStat}>{item.stats.conversations}</Text>
              <Text style={styles.platformSubStat}>Conversations</Text>
                </View>
            <View style={styles.statItem}>
              <Text style={styles.platformMainStat}>{item.stats.messages}</Text>
              <Text style={styles.platformSubStat}>Messages</Text>
                </View>
            </View>
          
          {/* Performance metrics */}
          {item.status === 'connected' && (
            <View style={styles.performanceMetrics}>
              <View style={styles.metricRow}>
                <Ionicons name="time-outline" size={12} color={colors.text.secondary} />
                <Text style={styles.metricText}>{item.stats.responseTime}</Text>
                    </View>
              <View style={styles.metricRow}>
                <Ionicons name="star-outline" size={12} color={colors.text.secondary} />
                <Text style={styles.metricText}>{item.stats.satisfaction}/5.0</Text>
                    </View>
              <View style={styles.metricRow}>
                <Ionicons name="trending-up-outline" size={12} color={colors.success} />
                <Text style={[styles.metricText, { color: colors.success }]}>+{item.stats.monthlyGrowth}%</Text>
                </View>
                </View>
          )}
                </View>

        {/* Progress bar for connected platforms */}
        {item.status === 'connected' && (
          <View style={styles.progressSection}>
            <View style={styles.progressBar}>
              <View 
                style={[
                  styles.progressFill, 
                  { 
                    width: `${Math.min(item.stats.satisfaction * 20, 100)}%`,
                    backgroundColor: item.color 
                  }
                ]} 
              />
                </View>
            <Text style={styles.progressText}>Performance</Text>
                </View>
        )}

        {/* Features list */}
        <View style={styles.featuresList}>
          {item.features.slice(0, 2).map((feature: string, index: number) => (
            <View key={index} style={styles.featureItem}>
              <Ionicons name="checkmark" size={10} color={colors.success} />
              <Text style={styles.featureText}>{feature}</Text>
            </View>
          ))}
                </View>

        {/* Last active */}
        <View style={styles.lastActiveRow}>
          <Ionicons name="time-outline" size={10} color={colors.text.secondary} />
          <Text style={styles.lastActiveText}>{item.lastActive}</Text>
                </View>
      </TouchableOpacity>
    );
  };

  // Render quick action card - using Home screen's pattern
  const renderQuickAction = ({ item }: { item: any }) => (
    <TouchableOpacity
      style={styles.quickActionCard}
      onPress={() => handleQuickAction(item.id)}
      activeOpacity={0.7}
    >
      <View style={styles.actionIcon}>
        <Ionicons name={item.icon} size={24} color={colors.primary} />
                </View>
      <Text style={styles.actionTitle}>{item.title}</Text>
    </TouchableOpacity>
  );

  // Create styles with dynamic insets - using Home screen's proven approach
const styles = StyleSheet.create({
    content: {
        flex: 1,
        marginTop: 20,
        //paddingHorizontal: isMobileBrowser ? 0 : (Platform.OS === 'web' ? Math.min(screenWidth * 0.1, 120) : spacing.screenPadding),
      paddingBottom: 100, // Match Home screen padding
        maxWidth: isMobileBrowser ? undefined : (Platform.OS === 'web' ? 1400 : undefined),
        alignSelf: isMobileBrowser ? undefined : (Platform.OS === 'web' ? 'center' : undefined),
        width: isMobileBrowser ? undefined : (Platform.OS === 'web' ? '100%' : undefined),
    },
    section: {
      marginBottom: spacing.xl,
    },
    sectionTitle: {
        fontSize: typography.fontSize.base,
        color: colors.textDark,
      marginBottom: spacing.md,
        fontFamily: typography.fontFamily.bold,
    },
    platformsList: {
      paddingHorizontal: spacing.xs,
      paddingBottom: spacing.sm, // Add bottom padding to prevent cutoff
    },
    loadingContainer: {
      padding: 20,
        alignItems: 'center',
    },
    loadingText: {
        fontSize: typography.fontSize.sm,
      color: colors.text.secondary,
    },
    emptyContainer: {
      padding: 20,
        alignItems: 'center',
    },
    emptyText: {
      fontSize: typography.fontSize.base,
      color: colors.text.secondary,
      marginBottom: 4,
    },
    emptySubText: {
        fontSize: typography.fontSize.sm,
      color: colors.text.secondary,
    },
    platformWidget: {
        backgroundColor: colors.background.surface,
        borderRadius: spacing.borderRadius.lg,
      padding: spacing.lg,
      marginRight: spacing.md,
      width: getCardWidth('platform'),
      minHeight: Platform.OS === 'web' ? 200 : 180,
      shadowColor: colors.textDark,
        shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 2,
    },
    platformHeader: {
        flexDirection: 'row',
      justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: spacing.sm,
    },
    platformIconRow: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    platformIconContainer: {
      width: 40,
      height: 40,
      borderRadius: 20,
      alignItems: 'center',
        justifyContent: 'center',
    },
    healthIndicator: {
      width: 16,
      height: 16,
      borderRadius: 8,
        alignItems: 'center',
      justifyContent: 'center',
      marginLeft: -8,
      marginTop: -8,
      borderWidth: 2,
      borderColor: colors.background.surface,
    },
    statusBadge: {
      paddingHorizontal: spacing.sm,
      paddingVertical: spacing.xs,
      borderRadius: spacing.borderRadius.sm,
    },
    statusText: {
      fontSize: typography.fontSize.xs,
      color: colors.white,
      fontFamily: typography.fontFamily.medium,
    },
    platformTitle: {
        fontSize: typography.fontSize.sm,
        color: colors.textDark,
      fontFamily: typography.fontFamily.medium,
      textAlign: 'center',
      marginBottom: spacing.sm,
    },
    platformStats: {
      marginBottom: spacing.sm,
    },
    statRow: {
        flexDirection: 'row',
        justifyContent: 'space-around',
        marginBottom: spacing.sm,
    },
    statItem: {
        alignItems: 'center',
    },
    platformMainStat: {
      fontSize: typography.fontSize.lg,
        color: colors.primary,
        fontFamily: typography.fontFamily.bold,
      marginBottom: 2,
    },
    platformSubStat: {
        fontSize: typography.fontSize.xs,
        color: colors.textLight,
        fontFamily: typography.fontFamily.primary,
    },
    performanceMetrics: {
        flexDirection: 'row',
        justifyContent: 'space-between',
      marginTop: spacing.xs,
    },
    metricRow: {
        flexDirection: 'row',
        alignItems: 'center',
      gap: 4,
    },
    metricText: {
        fontSize: typography.fontSize.xs,
      color: colors.text.secondary,
      fontFamily: typography.fontFamily.primary,
    },
    progressSection: {
      marginBottom: spacing.sm,
    },
    progressBar: {
      height: 4,
      backgroundColor: colors.border,
      borderRadius: 2,
      marginBottom: 4,
    },
    progressFill: {
      height: '100%',
      borderRadius: 2,
    },
    progressText: {
        fontSize: typography.fontSize.xs,
      color: colors.text.secondary,
      textAlign: 'center',
    },
    featuresList: {
        marginBottom: spacing.sm,
    },
    featureItem: {
        flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 2,
      gap: 4,
    },
    featureText: {
      fontSize: typography.fontSize.xs,
      color: colors.text.secondary,
      fontFamily: typography.fontFamily.primary,
    },
    lastActiveRow: {
        flexDirection: 'row',
        alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
    },
    lastActiveText: {
        fontSize: typography.fontSize.xs,
      color: colors.text.secondary,
      fontFamily: typography.fontFamily.primary,
    },
    quickActionsList: {
      paddingHorizontal: Platform.OS === 'web' ? spacing.md : spacing.md, // More padding on mobile
      paddingBottom: spacing.sm, // Add bottom padding to prevent cutoff
      gap: isMobileBrowser ? 8 : (Platform.OS === 'web' ? spacing.xs : spacing.sm), // More gap on mobile (12px instead of 6px)
    },
    quickActionCard: {
        backgroundColor: colors.background.surface,
      borderRadius: spacing.borderRadius.lg,
      padding: Platform.OS === 'web' ? spacing.lg : spacing.md, // More comfortable padding on mobile
        alignItems: 'center',
        justifyContent: 'center',
      width: getCardWidth('action'),
      minHeight: Platform.OS === 'web' ? 100 : 95, // Taller on mobile for better proportions
      shadowColor: colors.textDark,
        shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 2,
    },
    actionIcon: {
      marginBottom: spacing.sm, // Consistent spacing between icon and text
    },
    actionTitle: {
        fontSize: typography.fontSize.sm,
        color: colors.textDark,
        textAlign: 'center',
      fontFamily: typography.fontFamily.medium,
      marginBottom: 0,
      paddingHorizontal: Platform.OS === 'web' ? 0 : spacing.xs, // Add horizontal padding on mobile for text breathing room
      lineHeight: Platform.OS === 'web' ? 16 : 14, // Better line height for single line
      // Ensure text doesn't get cut off on mobile
      flexWrap: 'wrap',
    },
    // Modal styles
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
    },
    modalContent: {
        backgroundColor: colors.background.surface,
      borderRadius: spacing.borderRadius.lg,
      padding: spacing.lg,
      width: '90%',
      maxWidth: 400,
    },
    modalTitle: {
        fontSize: typography.fontSize.lg,
        fontFamily: typography.fontFamily.bold,
        color: colors.textDark,
      marginBottom: spacing.md,
      textAlign: 'center',
    },
    input: {
        borderWidth: 1,
      borderColor: colors.border,
        borderRadius: spacing.borderRadius.md,
      padding: spacing.md,
        marginBottom: spacing.md,
      fontSize: typography.fontSize.base,
    },
    button: {
        backgroundColor: colors.primary,
      padding: spacing.md,
        borderRadius: spacing.borderRadius.md,
        alignItems: 'center',
      marginBottom: spacing.sm,
    },
    buttonText: {
        color: colors.white,
        fontSize: typography.fontSize.base,
      fontFamily: typography.fontFamily.medium,
    },
    xQuickCard: {
      backgroundColor: colors.background.surface,
      borderRadius: spacing.borderRadius.lg,
      padding: spacing.lg,
      marginHorizontal: spacing.sm,
      shadowColor: colors.textDark,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.08,
      shadowRadius: 4,
      elevation: 2,
    },
    xQuickHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: spacing.md,
    },
    xQuickIconWrap: {
      width: 32,
      height: 32,
      borderRadius: 16,
      backgroundColor: '#000000',
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: spacing.sm,
    },
    xQuickHeaderTextWrap: {
      flex: 1,
    },
    xQuickTitle: {
      fontSize: typography.fontSize.base,
      color: colors.textDark,
      fontFamily: typography.fontFamily.bold,
      marginBottom: 2,
    },
    xQuickSubtitle: {
      fontSize: typography.fontSize.xs,
      color: colors.text.secondary,
      fontFamily: typography.fontFamily.primary,
      lineHeight: 16,
    },
    xQuickActionsRow: {
      flexDirection: 'row',
      gap: spacing.sm,
      flexWrap: 'wrap',
    },
    xPrimaryAction: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#000000',
      borderRadius: spacing.borderRadius.md,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
      gap: 6,
    },
    xPrimaryActionText: {
      color: '#FFFFFF',
      fontSize: typography.fontSize.sm,
      fontFamily: typography.fontFamily.medium,
    },
    xSecondaryAction: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: spacing.borderRadius.md,
      borderWidth: 1,
      borderColor: colors.border,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
      gap: 6,
      backgroundColor: '#F8FAFC',
    },
    xSecondaryActionText: {
      color: colors.primary,
      fontSize: typography.fontSize.sm,
      fontFamily: typography.fontFamily.medium,
    },
});

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      showRightPane={showRightPane}
      onRightPaneClose={() => {}}
    >
      <ScrollView 
        style={styles.content} 
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={[colors.primary]}
            tintColor={colors.primary}
          />
        }
      >
        {/* X Setup & Developer Entry */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>X Setup & Developer</Text>
          <View style={styles.xQuickCard}>
            <View style={styles.xQuickHeader}>
              <View style={styles.xQuickIconWrap}>
                <Ionicons name="logo-twitter" size={18} color="#FFFFFF" />
              </View>
              <View style={styles.xQuickHeaderTextWrap}>
                <Text style={styles.xQuickTitle}>X (Twitter) Integration</Text>
                <Text style={styles.xQuickSubtitle}>
                  {connectedPlatforms.find(p => p.id === 'x-twitter')?.status === 'connected'
                    ? 'Connected. Tap to manage account and sync settings.'
                    : 'Not connected yet. Tap below to login your X account.'}
                </Text>
              </View>
            </View>

            <View style={styles.xQuickActionsRow}>
              <TouchableOpacity
                style={styles.xPrimaryAction}
                onPress={() => navigateTo('x-twitter-setup')}
                activeOpacity={0.8}
              >
                <Ionicons name="log-in-outline" size={16} color="#FFFFFF" />
                <Text style={styles.xPrimaryActionText}>
                  {connectedPlatforms.find(p => p.id === 'x-twitter')?.status === 'connected' ? 'Manage X Account' : 'Login X Account'}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.xSecondaryAction}
                onPress={openDeveloperPlayground}
                activeOpacity={0.8}
              >
                <Ionicons name="code-slash-outline" size={16} color={colors.primary} />
                <Text style={styles.xSecondaryActionText}>Developer Playground</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {/* Header Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Connected Platforms</Text>
          {loading ? (
            <View style={styles.loadingContainer}>
              <Text style={styles.loadingText}>Loading platforms...</Text>
            </View>
          ) : (
            <FlatList
              data={connectedPlatforms}
              renderItem={renderPlatformWidget}
              keyExtractor={(item) => item.id}
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.platformsList}
            />
          )}
        </View>

        {/* Quick Actions Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Quick Actions</Text>
          <FlatList
            data={quickActions}
            renderItem={renderQuickAction}
            keyExtractor={(item) => item.id}
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.quickActionsList}
          />
        </View>
      </ScrollView>

      {/* Platform Modal */}
      <Modal
        visible={selectedPlatform !== null}
        transparent
        animationType="fade"
        onRequestClose={closePlatformModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Connect {selectedPlatform}</Text>
            <TextInput
              style={styles.input}
              placeholder="Account Name"
              value={accountName}
              onChangeText={setAccountName}
            />
            <TouchableOpacity
              style={styles.button}
              onPress={() => {
                setShowAddAccount(true);
                closePlatformModal();
              }}
            >
              <Text style={styles.buttonText}>Continue</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, { backgroundColor: colors.text.secondary }]}
              onPress={closePlatformModal}
            >
              <Text style={styles.buttonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Add Account Modal */}
      <Modal
        visible={showAddAccount}
        transparent
        animationType="fade"
        onRequestClose={closeAddAccountModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Add WhatsApp Account</Text>
            <TextInput
              style={styles.input}
              placeholder="Phone Number"
              value={phoneNumber}
              onChangeText={setPhoneNumber}
              keyboardType="phone-pad"
            />
            <TouchableOpacity
              style={styles.button}
              onPress={() => {
                setShowVerification(true);
                closeAddAccountModal();
              }}
            >
              <Text style={styles.buttonText}>Verify Number</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, { backgroundColor: colors.text.secondary }]}
              onPress={closeAddAccountModal}
            >
              <Text style={styles.buttonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Verification Modal */}
      <Modal
        visible={showVerification}
        transparent
        animationType="fade"
        onRequestClose={closeVerificationModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Verify Phone Number</Text>
            <Text style={styles.emptyText}>Enter the verification code sent to {phoneNumber}</Text>
            <TextInput
              style={styles.input}
              placeholder="Verification Code"
              value={verificationCode}
              onChangeText={setVerificationCode}
              keyboardType="number-pad"
            />
            <TouchableOpacity
              style={styles.button}
              onPress={() => {
                showNotification('success', 'Success', 'WhatsApp account connected successfully!');
                closeVerificationModal();
              }}
            >
              <Text style={styles.buttonText}>Verify & Connect</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, { backgroundColor: colors.text.secondary }]}
              onPress={closeVerificationModal}
            >
              <Text style={styles.buttonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </ScreenLayout>
  );
};